/**
 * Auth Context - Clerk Integration
 * Provides authentication state and utilities using Clerk
 */
import { createContext, useContext, ReactNode } from 'react'
import { useUser, useAuth as useClerkAuth, useClerk } from '@clerk/clerk-react'
import { useQuery, useMutation } from 'convex/react'
import { api } from '../../convex/_generated/api'
import type { Id } from '../../convex/_generated/dataModel'

interface User {
  id: Id<"users"> | null
  _id: Id<"users"> | null
  clerkId: string
  name: string
  email: string
  role: string
  isVerified: boolean
  language?: string
  displayName?: string
  username?: string
  bio?: string
  imageUrl?: string
}

interface AuthContextType {
  user: User | null
  convexUserId: Id<"users"> | null
  isAuthenticated: boolean
  isLoading: boolean
  login: () => void
  logout: () => Promise<void>
  openSignIn: () => void
  openSignUp: () => void
  openUserProfile: () => void
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const { user: clerkUser, isLoaded: clerkLoaded, isSignedIn } = useUser()
  const { signOut } = useClerkAuth()
  const { openSignIn, openSignUp, openUserProfile } = useClerk()
  
  // Get user from Convex database by Clerk ID
  const convexUser = useQuery(
    api.users.getByClerkId,
    isSignedIn && clerkUser ? {
      clerkId: clerkUser.id,
    } : "skip"
  )
  
  // Create user mutation for new users
  const createFromClerk = useMutation(api.users.createFromClerk)

  const isLoading = !clerkLoaded || (isSignedIn && convexUser === undefined)
  
  // Auto-create user in Convex if signed in but no Convex user exists
  if (isSignedIn && clerkUser && convexUser === null) {
    createFromClerk({
      clerkId: clerkUser.id,
      email: clerkUser.primaryEmailAddress?.emailAddress || '',
      name: clerkUser.fullName || clerkUser.firstName || 'User',
      imageUrl: clerkUser.imageUrl,
    }).catch(console.error)
  }

  // Build user object from Clerk + Convex data
  const user: User | null = isSignedIn && clerkUser ? {
    id: convexUser?._id || null,
    _id: convexUser?._id || null,
    clerkId: clerkUser.id,
    name: clerkUser.fullName || clerkUser.firstName || 'User',
    email: clerkUser.primaryEmailAddress?.emailAddress || '',
    role: convexUser?.role || 'member',
    isVerified: clerkUser.primaryEmailAddress?.verification?.status === 'verified',
    language: convexUser?.language || 'en',
    displayName: clerkUser.fullName || clerkUser.username || undefined,
    username: clerkUser.username || undefined,
    bio: convexUser?.bio || undefined,
    imageUrl: clerkUser.imageUrl,
  } : null

  const login = () => {
    openSignIn()
  }

  const logout = async () => {
    await signOut()
  }

  return (
    <AuthContext.Provider value={{
      user,
      convexUserId: convexUser?._id || null,
      isAuthenticated: isSignedIn ?? false,
      isLoading,
      login,
      logout,
      openSignIn,
      openSignUp,
      openUserProfile,
    }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
